<script setup>
import {computed, defineProps} from 'vue'

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
})

// flexform settings from CE (already flattened in PHP)
const settings = computed(() => props.card?.settings ?? {})

// variant as its own computed
const variant = computed(() =>
  (props.card?.variant || props.card?.content_type || '').toString().toLowerCase()
)

// root classes – same visibility flags pattern
const rootClasses = computed(() => {
  const cls = ['content-item', 'module-promocard']

  if (variant.value) {
    cls.push(`module-promocard--${variant.value}`)
  }

  if (settings.value.hideForABTest) cls.push('-hide-ab-test')
  if (settings.value.hideMobile) cls.push('-hide-mobile')
  if (settings.value.hideTablet) cls.push('-hide-tablet')
  if (settings.value.hideDesktop) cls.push('-hide-desktop')

  return cls.join(' ')
})

const layoutPro = computed(() => ['pro'].includes(variant.value))
const layoutOffer = computed(() => ['offer'].includes(variant.value))
const useBlackButton = computed(() => ['pro', 'benefits'].includes(variant.value))

// get image from flattened media array
const getImageForCard = (card, fieldName = 'image') => {
  if (!card) return null

  const media = Array.isArray(card.media) ? card.media : []
  const fromMedia = media.find(m => (m.field || '').toLowerCase() === fieldName)

  if (fromMedia) {
    return {
      url: fromMedia.url,
      alt: fromMedia.alt || card.title || '',
      title: fromMedia.title || '',
    }
  }

  const direct = card[fieldName]
  if (direct?.url) {
    return {
      url: direct.url,
      alt: direct.alternative || card.title || '',
      title: direct.title || '',
    }
  }

  return null
}

const image = computed(() => getImageForCard(props.card, 'image'))
const imageMobile = computed(() => getImageForCard(props.card, 'image_mobile'))
</script>

<template>
  <div
    :class="rootClasses"
    :id="settings.anchor || null"
  >
    <div class="promocard--wrapper">
      <component
        :is="card.link ? 'a' : 'div'"
        class="promocard__link"
        :href="card.link || undefined"
      >
        <article class="promocard">
          <div class="promocard__content">
            <div class="promocard__headlines" v-if="card.title || card.subtitle">
              <span
                v-if="card.title"
                class="promocard__title heading"
                v-html="card.title"
              ></span>
              <p
                v-if="card.subtitle"
                class="promocard__subtitle"
              >
                {{ card.subtitle }}
              </p>
            </div>
            <div
              v-if="card.bodytext"
              class="promocard__body"
              v-html="card.bodytext"
            ></div>
            <div
              v-if="image && layoutOffer"
              class="promocard__image--offer"
            >
              <picture>
                <source
                  v-if="imageMobile"
                  media="(max-width: 767px)"
                  :srcset="imageMobile.url"
                />
                <img
                  :src="image.url"
                  :alt="image.alt"
                  :title="image.title"
                  loading="lazy"
                />
              </picture>
            </div>
            <div
              v-if="card.price || card.price_old"
              class="promocard__prices"
            >
              <span
                v-if="card.price_old"
                class="promocard__price-old"
              >
                {{ card.price_old }}
              </span>
              <span v-if="card.price" class="promocard__price">
                <span class="promocard__price-label">
                  {{ card.price }}
                </span>
                <span class="promocard__price-triangle" aria-hidden="true"></span>
              </span>
            </div>
            <div
              v-if="card.button_label"
              class="promocard__button-wrapper"
            >
              <div :class="['-btn', useBlackButton ? '-btn-black' : '-btn-alpha']">
                <span>
                  {{ card.button_label }}
                </span>
              </div>
            </div>
          </div>
        </article>
      </component>
      <div v-if="image && layoutPro" class="promocard__image">
        <picture>
          <source
            v-if="imageMobile"
            media="(max-width: 767px)"
            :srcset="imageMobile.url"
          />
          <img
            :src="image.url"
            :alt="image.alt"
            :title="image.title"
            loading="lazy"
          />
        </picture>
      </div>
      <div v-if="layoutPro" class="promocard__pro--background"></div>
      <div v-else class="promocard__background"></div>
    </div>
  </div>
</template>
